import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInvoice } from '@/hooks/useInvoice';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Download,
  Eye,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Share2,
  ChevronDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import { exportInvoiceToPDF } from '@/lib/export';
import { exportToWord } from '@/lib/exportUtils';
import { InvoicePrintView } from '@/components/invoice/InvoicePrintView';
import type { Invoice } from '@/types/invoice';

export default function InvoiceList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invoices, loading, deleteInvoice, getInvoice, incrementDownloadCount, updateInvoice } = useInvoice();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'number' | 'amount'>('date');
  const [activeExportInvoice, setActiveExportInvoice] = useState<Invoice | null>(null);

  // Filter and sort invoices
  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (inv) =>
          inv.invoiceNumber?.toLowerCase().includes(query) ||
          inv.billTo?.name?.toLowerCase().includes(query) ||
          inv.billTo?.email?.toLowerCase().includes(query) ||
          inv.billTo?.companyName?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return (b.issueDate?.getTime() || 0) - (a.issueDate?.getTime() || 0);
      } else if (sortBy === 'number') {
        return (b.invoiceNumber || '').localeCompare(a.invoiceNumber || '');
      } else if (sortBy === 'amount') {
        return (b.totalAmount || 0) - (a.totalAmount || 0);
      }
      return 0;
    });

    return filtered;
  }, [invoices, searchQuery, statusFilter, sortBy]);

  const getStatusBadge = (status: string, invoiceId: string) => {
    const config: Record<string, { variant: any; icon: any; color: string }> = {
      paid: { variant: 'default', icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200' },
      sent: { variant: 'default', icon: Clock, color: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200' },
      draft: { variant: 'default', icon: AlertCircle, color: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200' },
      overdue: { variant: 'default', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' },
      cancelled: { variant: 'default', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' },
    };

    const statusConfig = config[status] || config.draft;
    const Icon = statusConfig.icon;

    const statuses = ['draft', 'sent', 'paid', 'cancelled'] as const;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="outline-none focus:outline-none border-0 p-0 m-0 bg-transparent inline-flex align-middle cursor-pointer">
          <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit border cursor-pointer transition-all hover:scale-105`}>
            <Icon className="h-3 w-3" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
            <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {statuses.map((s) => (
            <DropdownMenuItem
              key={s}
              onSelect={() => handleUpdateInvoiceStatus(invoiceId, s)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span className={`inline-flex h-2 w-2 rounded-full ${s === 'paid' ? 'bg-green-500' : s === 'sent' ? 'bg-blue-500' : s === 'cancelled' || (s as string) === 'overdue' ? 'bg-red-500' : 'bg-gray-400'}`} />
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleUpdateInvoiceStatus = async (invoiceId: string, newStatus: any) => {
    try {
      await updateInvoice(invoiceId, { status: newStatus });
    } catch (error) {
      console.error('Failed to update invoice status:', error);
    }
  };

  const handleDelete = async (invoiceId: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      await deleteInvoice(invoiceId);
    }
  };

  const handleDownloadPDF = async (invoiceId: string) => {
    try {
      const invoice = await getInvoice(invoiceId);
      if (!invoice) {
        toast({
          title: 'Error',
          description: 'Invoice not found',
          variant: 'destructive',
        });
        return;
      }

      setActiveExportInvoice(invoice);
      // Wait for React to render the component off-screen
      await new Promise<void>((resolve) => setTimeout(resolve, 200));

      await exportInvoiceToPDF(invoice, 'invoice-export-content');
      
      await incrementDownloadCount();
      toast({
        title: 'Success',
        description: 'Invoice downloaded successfully!',
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download invoice. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setActiveExportInvoice(null);
    }
  };

  const handleDownloadWord = async (invoiceId: string) => {
    try {
      const invoice = await getInvoice(invoiceId);
      if (!invoice) {
        toast({
          title: 'Error',
          description: 'Invoice not found',
          variant: 'destructive',
        });
        return;
      }

      const filename = `${invoice.invoiceNumber || 'invoice'}.docx`;
      await exportToWord(invoice, filename);
      
      await incrementDownloadCount();
      toast({
        title: 'Success',
        description: 'Word document downloaded successfully!',
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download Word document. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleShare = (invoice: Invoice) => {
    if (!user) return;
    const shareUrl = `${window.location.origin}/share/invoice/${user.uid}/${invoice.invoiceId}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: 'Link Copied',
      description: 'Shareable invoice link copied to clipboard!',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Invoices</h1>
          <p className="text-muted-foreground mt-1">
            View and manage all your invoices
          </p>
        </div>
        <Link to="/invoices/new">
          <Button size="lg">
            <Plus className="mr-2 h-5 w-5" />
            Create Invoice
          </Button>
        </Link>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by invoice number, client name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Latest First</SelectItem>
                <SelectItem value="number">Invoice Number</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      {filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">
                {searchQuery || statusFilter !== 'all'
                  ? 'No invoices found'
                  : 'No invoices yet'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : 'Create your first invoice to get started'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Link to="/invoices/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Invoice
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold">Invoice #</th>
                      <th className="text-left p-4 font-semibold">Client</th>
                      <th className="text-left p-4 font-semibold">Issue Date</th>
                      <th className="text-left p-4 font-semibold">Due Date</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-right p-4 font-semibold">Amount</th>
                      <th className="text-right p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                      <tr
                        key={invoice.invoiceId}
                        className="border-b hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-4">
                          <Link
                            to={`/invoices/edit/${invoice.invoiceId}`}
                            className="font-semibold text-primary hover:underline"
                          >
                            {invoice.invoiceNumber}
                          </Link>
                        </td>
                        <td className="p-4">
                          <div>
                            <div className="font-medium">
                              {invoice.billTo?.name || invoice.billTo?.companyName || 'No client'}
                            </div>
                            {invoice.billTo?.email && (
                              <div className="text-sm text-muted-foreground">
                                {invoice.billTo.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          {invoice.issueDate ? format(invoice.issueDate, 'MMM dd, yyyy') : 'N/A'}
                        </td>
                        <td className="p-4 text-sm">
                          {invoice.dueDate ? format(invoice.dueDate, 'MMM dd, yyyy') : 'N/A'}
                        </td>
                        <td className="p-4">{getStatusBadge(invoice.status, invoice.invoiceId)}</td>
                        <td className="p-4 text-right font-semibold">
                          {formatCurrency(invoice.totalAmount || 0, invoice.currency)}
                        </td>
                        <td className="p-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => navigate(`/invoices/edit/${invoice.invoiceId}`)}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => navigate(`/invoices/preview/${invoice.invoiceId}`)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDownloadPDF(invoice.invoiceId)}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleShare(invoice)}
                                disabled={!isPro}
                                className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                              >
                                <div className="flex items-center">
                                  <Share2 className="h-4 w-4 mr-2" />
                                  Share Link
                                </div>
                                {!isPro && (
                                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDownloadWord(invoice.invoiceId)}
                                disabled={!isPro}
                                className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                              >
                                <div className="flex items-center">
                                  <Download className="h-4 w-4 mr-2" />
                                  Download Word
                                </div>
                                {!isPro && (
                                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDelete(invoice.invoiceId)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {filteredInvoices.map((invoice) => (
              <Card key={invoice.invoiceId}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Link
                        to={`/invoices/edit/${invoice.invoiceId}`}
                        className="font-semibold text-lg text-primary hover:underline"
                      >
                        {invoice.invoiceNumber || 'Untitled'}
                      </Link>
                      <p className="text-sm text-muted-foreground mt-1">
                        {invoice.billTo?.name || invoice.billTo?.companyName || 'No client'}
                      </p>
                    </div>
                    {getStatusBadge(invoice.status, invoice.invoiceId)}
                  </div>

                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Issue Date:</span>
                      <span>{invoice.issueDate ? format(invoice.issueDate, 'MMM dd, yyyy') : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Due Date:</span>
                      <span>{invoice.dueDate ? format(invoice.dueDate, 'MMM dd, yyyy') : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base pt-2 border-t">
                      <span>Amount:</span>
                      <span>{formatCurrency(invoice.totalAmount || 0, invoice.currency)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/invoices/edit/${invoice.invoiceId}`)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/invoices/preview/${invoice.invoiceId}`)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleDownloadPDF(invoice.invoiceId)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                         <DropdownMenuItem
                           onClick={() => handleShare(invoice)}
                           disabled={!isPro}
                           className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                         >
                           <div className="flex items-center">
                             <Share2 className="h-4 w-4 mr-2" />
                             Share Link
                           </div>
                           {!isPro && (
                             <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                           )}
                         </DropdownMenuItem>
                         <DropdownMenuItem
                           onClick={() => handleDownloadWord(invoice.invoiceId)}
                           disabled={!isPro}
                           className={!isPro ? 'opacity-60 cursor-not-allowed flex items-center justify-between w-full' : ''}
                         >
                           <div className="flex items-center">
                             <Download className="h-4 w-4 mr-2" />
                             Download Word
                           </div>
                           {!isPro && (
                             <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-[10px] py-0 px-1">PRO</Badge>
                           )}
                         </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(invoice.invoiceId)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 text-sm text-muted-foreground text-center">
            Showing {filteredInvoices.length} of {invoices.length} invoice(s)
          </div>
        </>
      )}

      {/* Hidden container used to render invoice with dynamic styles for high-fidelity PDF capture */}
      {activeExportInvoice && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '794px' }}>
          <InvoicePrintView invoice={activeExportInvoice} elementId="invoice-export-content" />
        </div>
      )}
    </div>
  );
}
